import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { BookOpen } from '@phosphor-icons/react';
import { ChartColor } from '@t-one-internal/design-system';

interface TopCourseRow {
    courseTitle: string;
    courseMode?: string;
    enrollmentCount: number;
    completedCount?: number;
    completionRate?: number;
    avgProgress?: number;
}

interface OrgTopCoursesWidgetProps {
    data?: TopCourseRow[] | null;
    loading?: boolean;
    error?: string;
}

export const OrgTopCoursesWidget: React.FC<OrgTopCoursesWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data.slice(0, 5) : [];

    const chartData = rows.map(r => ({
        name: (r.courseTitle || '').length > 20 ? r.courseTitle.slice(0, 20) + '...' : r.courseTitle,
        fullName: r.courseTitle || '',
        enrollments: Number(r.enrollmentCount) || 0,
        completed: Number(r.completedCount) || 0,
    }));

    return (
        <div className="flex flex-col h-full p-5">
<div className="flex-1">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <BookOpen className="w-8 h-8" />
                        <span className="text-sm">No course data</span>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (!active || !payload?.length) return null;
                                    const d = payload[0].payload;
                                    return (
                                        <div style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxWidth: '280px' }}>
                                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#111827', marginBottom: '6px', wordBreak: 'break-word' }}>{d.fullName}</div>
                                            <div style={{ fontSize: '11px', color: ChartColor.blue }}>Enrolled: {d.enrollments}</div>
                                            <div style={{ fontSize: '11px', color: ChartColor.emerald }}>Completed: {d.completed}</div>
                                        </div>
                                    );
                                }}
                            />
                            <Bar dataKey="enrollments" name="Enrolled" fill={ChartColor.blue} radius={[0, 4, 4, 0]} barSize={16} />
                            <Bar dataKey="completed" name="Completed" fill={ChartColor.emerald} radius={[0, 4, 4, 0]} barSize={16} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};
